import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, RefreshCwIcon, FileEditIcon, FilterIcon, XIcon, ChevronDownIcon, LockIcon, UserIcon, SettingsIcon, CheckIcon, GripVerticalIcon } from 'lucide-react';
import { Invoice } from './documentReviewTypes';
interface DocumentSidebarProps {
  documents: Invoice[];
  selectedDocumentId: string;
  onSelectDocument: (id: string) => void;
  currentDocument?: any;
}
const hasDraft = (documentId: string): boolean => {
  return localStorage.getItem(`invoice_draft_${documentId}`) !== null;
};
type FilterCategory = 'type' | 'status' | 'team';
interface ActiveFilters {
  type: string[];
  status: string[];
  team: string[];
}
const FILTERS_STORAGE_KEY = 'document_filters';
type DocumentType = 'PO Invoice' | 'Non-PO Invoice' | 'Credit Note';
interface TileField {
  id: string;
  label: string;
  enabled: boolean;
  order: number;
}
interface TileConfiguration {
  [key: string]: TileField[];
}
const TILE_CONFIG_STORAGE_KEY = 'document_tile_config';
const DEFAULT_TILE_CONFIG: TileConfiguration = {
  'PO Invoice': [{
    id: 'vendor',
    label: 'Vendor',
    enabled: true,
    order: 0
  }, {
    id: 'documentNo',
    label: 'Document Number',
    enabled: true,
    order: 1
  }, {
    id: 'receivedDate',
    label: 'Received Date',
    enabled: true,
    order: 2
  }, {
    id: 'documentDate',
    label: 'Document Date',
    enabled: true,
    order: 3
  }, {
    id: 'dueDate',
    label: 'Due Date',
    enabled: true,
    order: 4
  }, {
    id: 'poNo',
    label: 'PO Number',
    enabled: true,
    order: 5
  }, {
    id: 'team',
    label: 'Assigned To',
    enabled: true,
    order: 6
  }, {
    id: 'amount',
    label: 'Amount',
    enabled: true,
    order: 7
  }, {
    id: 'tag',
    label: 'Tag(s)',
    enabled: false,
    order: 8
  }],
  'Non-PO Invoice': [{
    id: 'vendor',
    label: 'Vendor',
    enabled: true,
    order: 0
  }, {
    id: 'documentNo',
    label: 'Document Number',
    enabled: true,
    order: 1
  }, {
    id: 'receivedDate',
    label: 'Received Date',
    enabled: true,
    order: 2
  }, {
    id: 'documentDate',
    label: 'Document Date',
    enabled: true,
    order: 3
  }, {
    id: 'dueDate',
    label: 'Due Date',
    enabled: true,
    order: 4
  }, {
    id: 'team',
    label: 'Assigned To',
    enabled: true,
    order: 5
  }, {
    id: 'amount',
    label: 'Amount',
    enabled: true,
    order: 6
  }, {
    id: 'tag',
    label: 'Tag(s)',
    enabled: false,
    order: 7
  }, {
    id: 'poNo',
    label: 'PO Number',
    enabled: false,
    order: 8
  }],
  'Credit Note': [{
    id: 'vendor',
    label: 'Vendor',
    enabled: true,
    order: 0
  }, {
    id: 'documentNo',
    label: 'Document Number',
    enabled: true,
    order: 1
  }, {
    id: 'receivedDate',
    label: 'Received Date',
    enabled: false,
    order: 2
  }, {
    id: 'documentDate',
    label: 'Document Date',
    enabled: true,
    order: 3
  }, {
    id: 'dueDate',
    label: 'Due Date',
    enabled: false,
    order: 4
  }, {
    id: 'poNo',
    label: 'PO Number',
    enabled: false,
    order: 5
  }, {
    id: 'team',
    label: 'Assigned To',
    enabled: true,
    order: 6
  }, {
    id: 'amount',
    label: 'Credit Amount',
    enabled: true,
    order: 7
  }, {
    id: 'tag',
    label: 'Tag(s)',
    enabled: false,
    order: 8
  }]
};
const loadTileConfig = (): TileConfiguration => {
  try {
    const saved = localStorage.getItem(TILE_CONFIG_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (error) {
    console.error('Failed to load tile config:', error);
  }
  return DEFAULT_TILE_CONFIG;
};
const saveTileConfig = (config: TileConfiguration) => {
  try {
    localStorage.setItem(TILE_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Failed to save tile config:', error);
  }
};
const loadSavedFilters = (): ActiveFilters => {
  try {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (error) {
    console.error('Failed to load saved filters:', error);
  }
  return {
    type: [],
    status: [],
    team: []
  };
};
const saveFilters = (filters: ActiveFilters) => {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('Failed to save filters:', error);
  }
};
// Inline Badge component to keep it self-contained
const Badge = ({
  status,
  children
}: {
  status: string;
  children: React.ReactNode;
}) => {
  let colorClass = 'bg-gray-100 text-gray-800';
  if (status === 'Approved') colorClass = 'bg-green-100 text-green-800';
  if (status === 'Exception') colorClass = 'bg-red-100 text-red-800';
  if (status === 'In Progress') colorClass = 'bg-blue-100 text-blue-800';
  if (status === 'Pending Review') colorClass = 'bg-yellow-100 text-yellow-800';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`} data-id="element-5169">
      {children}
    </span>;
};
export function DocumentSidebar({
  documents,
  selectedDocumentId,
  onSelectDocument,
  currentDocument
}: DocumentSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(loadSavedFilters());
  const [tileConfig, setTileConfig] = useState<TileConfiguration>(loadTileConfig());
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configDocumentType, setConfigDocumentType] = useState<DocumentType>('PO Invoice');
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    documentType: DocumentType;
  } | null>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    saveFilters(activeFilters);
  }, [activeFilters]);
  useEffect(() => {
    saveTileConfig(tileConfig);
  }, [tileConfig]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        const filterButton = document.querySelector('[data-filter-button]');
        if (filterButton && filterButton.contains(event.target as Node)) return;
        setShowFilterMenu(false);
      }
    };
    if (showFilterMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterMenu]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
      }
    };
    if (contextMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);
  const handleContextMenu = (e: React.MouseEvent, documentType: DocumentType) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      documentType
    });
  };
  const openConfigModal = (documentType: DocumentType) => {
    setConfigDocumentType(documentType);
    setShowConfigModal(true);
    setContextMenu(null);
  };
  const toggleField = (documentType: DocumentType, fieldId: string) => {
    setTileConfig(prev => ({
      ...prev,
      [documentType]: prev[documentType].map(field => field.id === fieldId ? {
        ...field,
        enabled: !field.enabled
      } : field)
    }));
  };
  const resetToDefault = (documentType: DocumentType) => {
    setTileConfig(prev => ({
      ...prev,
      [documentType]: DEFAULT_TILE_CONFIG[documentType]
    }));
  };
  const getFieldValue = (document: Invoice, fieldId: string): string | null => {
    switch (fieldId) {
      case 'vendor':
        return document.vendor;
      case 'documentNo':
        return document.documentNo || null;
      case 'receivedDate':
        return document.receivedDate;
      case 'documentDate':
        return document.documentDate;
      case 'dueDate':
        return document.dueDate || null;
      case 'poNo':
        return document.poNo || null;
      case 'team':
        return document.team;
      case 'amount':
        return document.amount;
      case 'tag':
        return document.tag || null;
      default:
        return null;
    }
  };
  const filteredDocuments = documents.filter(document => {
    if (activeFilters.type.length > 0) {
      const matchesType = activeFilters.type.some(type => {
        if (type === 'po') return document.documentType === 'PO Invoice';
        if (type === 'non-po') return document.documentType === 'Non-PO Invoice';
        if (type === 'credit-note') return document.documentType === 'Credit Note';
        if (type === 'draft') return hasDraft(document.id) || document.status.toLowerCase().includes('draft');
        return false;
      });
      if (!matchesType) return false;
    }
    if (activeFilters.status.length > 0 && !activeFilters.status.includes(document.status)) return false;
    if (activeFilters.team.length > 0 && !activeFilters.team.includes(document.team)) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return document.vendor.toLowerCase().includes(query) || document.documentNo?.toLowerCase().includes(query) || document.poNo?.toLowerCase().includes(query) || document.amount.toLowerCase().includes(query);
    }
    return true;
  });
  const toggleFilter = (category: FilterCategory, value: string) => {
    setActiveFilters(prev => {
      const current = prev[category];
      const newFilters = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return {
        ...prev,
        [category]: newFilters
      };
    });
  };
  const clearAllFilters = () => {
    const emptyFilters = {
      type: [],
      status: [],
      team: []
    };
    setActiveFilters(emptyFilters);
    saveFilters(emptyFilters);
  };
  const hasActiveFilters = activeFilters.type.length > 0 || activeFilters.status.length > 0 || activeFilters.team.length > 0;
  const activeFilterCount = activeFilters.type.length + activeFilters.status.length + activeFilters.team.length;
  return <div className="w-72 bg-white border-r border-gray-200 flex flex-col h-screen" data-id="element-5170">
      <div className="p-4 border-b border-gray-200" data-id="element-5171">
        <div className="flex items-center justify-between mb-3" data-id="element-5172">
          <span className="text-sm text-gray-600" data-id="element-5173">
            {filteredDocuments.length} of {documents.length}
          </span>
          <button className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-5174">
            <RefreshCwIcon className="w-4 h-4 text-gray-600" data-id="element-5175" />
          </button>
        </div>
        <div className="relative mb-3" data-id="element-5176">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-5177" />
          <input type="text" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#7CB342] focus:border-transparent" data-id="element-5178" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded transition-colors" data-id="element-5179">
              <XIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-5180" />
            </button>}
        </div>
        <div className="relative" ref={filterMenuRef} data-id="element-5181">
          <button data-filter-button onClick={() => setShowFilterMenu(!showFilterMenu)} className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm font-medium transition-colors ${hasActiveFilters ? 'bg-[#7CB342] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-5182">
            <div className="flex items-center gap-2" data-id="element-5183">
              <FilterIcon className="w-4 h-4" data-id="element-5184" />
              <span data-id="element-5185">Filters</span>
              {activeFilterCount > 0 && <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs" data-id="element-5186">
                  {activeFilterCount}
                </span>}
            </div>
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} data-id="element-5187" />
          </button>
          {showFilterMenu && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto" data-id="element-5188">
              <div className="p-3 border-b border-gray-100" data-id="element-5189">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2" data-id="element-5190">
                  Document Type
                </div>
                <div className="space-y-1.5" data-id="element-5191">
                  {[{
                id: 'po',
                label: 'PO Invoice'
              }, {
                id: 'non-po',
                label: 'Non-PO Invoice'
              }, {
                id: 'credit-note',
                label: 'Credit Note'
              }, {
                id: 'draft',
                label: 'Has Draft'
              }].map(option => <label key={option.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer" data-id="element-5192">
                      <input type="checkbox" checked={activeFilters.type.includes(option.id)} onChange={() => toggleFilter('type', option.id)} className="w-4 h-4 text-[#7CB342] border-gray-300 rounded focus:ring-[#7CB342]" data-id="element-5193" />
                      <span className="text-sm text-gray-700 flex-1" data-id="element-5194">
                        {option.label}
                      </span>
                    </label>)}
                </div>
              </div>
              {hasActiveFilters && <div className="p-3 border-t border-gray-100" data-id="element-5195">
                  <button onClick={clearAllFilters} className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-5196">
                    Clear All Filters
                  </button>
                </div>}
            </div>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" data-id="element-5197">
        {filteredDocuments.length === 0 ? <div className="flex flex-col items-center justify-center py-12 px-4 text-center" data-id="element-5198">
            <FilterIcon className="w-8 h-8 text-gray-300 mb-2" data-id="element-5199" />
            <p className="text-sm text-gray-500" data-id="element-5200">No documents found</p>
          </div> : filteredDocuments.map(document => {
        const isDraft = hasDraft(document.id);
        const isLocked = !!document.lockedBy;
        const isLockedByCurrentUser = document.lockedBy === 'Current User';
        const docType = document.documentType as DocumentType;
        const config = tileConfig[docType] || [];
        const enabledFields = config.filter(field => field.enabled).sort((a, b) => a.order - b.order);
        return <div key={document.id} onClick={() => onSelectDocument(document.id)} onContextMenu={e => handleContextMenu(e, docType)} className={`p-4 border-b border-gray-200 cursor-pointer transition-colors ${selectedDocumentId === document.id ? 'bg-[#F1F8E9] border-l-4 border-l-[#7CB342]' : 'hover:bg-gray-50'} ${isLocked && !isLockedByCurrentUser ? 'opacity-60' : ''}`} data-id="element-5201">
                {isLocked && <div className={`flex items-center gap-2 mb-2 px-2 py-1.5 rounded text-xs font-medium ${isLockedByCurrentUser ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-5202">
                    <LockIcon className="w-3.5 h-3.5" data-id="element-5203" />
                    <UserIcon className="w-3.5 h-3.5" data-id="element-5204" />
                    <span data-id="element-5205">
                      {isLockedByCurrentUser ? 'You are editing this' : `Being edited by ${document.lockedBy}`}
                    </span>
                  </div>}
                <div className="flex items-center justify-between mb-2" data-id="element-5206">
                  <div className="text-sm font-medium text-gray-900" data-id="element-5207">
                    {document.documentType}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-5208">
                    <Badge status={document.status} data-id="element-5209">{document.status}</Badge>
                    {isDraft && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 rounded" data-id="element-5210">
                        <FileEditIcon size={10} className="text-amber-700" data-id="element-5211" />
                        <span className="text-xs font-medium text-amber-700" data-id="element-5212">
                          Draft
                        </span>
                      </div>}
                  </div>
                </div>
                <div className="space-y-1 text-xs text-gray-600" data-id="element-5213">
                  {enabledFields.map(field => {
              const value = getFieldValue(document, field.id);
              if (!value) return null;
              const isAmount = field.id === 'amount';
              const isVendor = field.id === 'vendor';
              const isTeam = field.id === 'team';
              return <div key={field.id} className="flex justify-between" data-id="element-5214">
                        <span data-id="element-5215">{field.label}:</span>
                        <span className={`${isAmount || isVendor || isTeam ? 'font-medium text-gray-900' : ''}`} data-id="element-5216">
                          {value}
                        </span>
                      </div>;
            })}
                </div>
                <div className="mt-2 text-xs text-gray-500" data-id="element-5217">
                  {document.timestamp}
                </div>
              </div>;
      })}
      </div>

      {contextMenu && <div ref={contextMenuRef} className="fixed bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-50 min-w-[180px]" style={{
      left: `${contextMenu.x}px`,
      top: `${contextMenu.y}px`
    }} data-id="element-5218">
          <button onClick={() => openConfigModal(contextMenu.documentType)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors" data-id="element-5219">
            <SettingsIcon className="w-4 h-4" data-id="element-5220" />
            <span data-id="element-5221">Configure {contextMenu.documentType} Tiles</span>
          </button>
        </div>}

      {showConfigModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-5222">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col" data-id="element-5223">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-5224">
              <div data-id="element-5225">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-5226">
                  Configure Tile Fields
                </h2>
                <p className="text-sm text-gray-600 mt-1" data-id="element-5227">
                  {configDocumentType}
                </p>
              </div>
              <button onClick={() => setShowConfigModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-5228">
                <XIcon className="w-5 h-5 text-gray-600" data-id="element-5229" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6" data-id="element-5230">
              <p className="text-sm text-gray-600 mb-4" data-id="element-5231">
                Select which fields to display on document tiles.
              </p>
              <div className="space-y-2" data-id="element-5232">
                {tileConfig[configDocumentType]?.sort((a, b) => a.order - b.order).map(field => <label key={field.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-5233">
                      <input type="checkbox" checked={field.enabled} onChange={() => toggleField(configDocumentType, field.id)} className="w-4 h-4 text-[#7CB342] border-gray-300 rounded focus:ring-[#7CB342]" data-id="element-5234" />
                      <GripVerticalIcon className="w-4 h-4 text-gray-400" data-id="element-5235" />
                      <span className="flex-1 text-sm text-gray-900" data-id="element-5236">
                        {field.label}
                      </span>
                      {field.enabled && <CheckIcon className="w-4 h-4 text-[#7CB342]" data-id="element-5237" />}
                    </label>)}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-5238">
              <button onClick={() => resetToDefault(configDocumentType)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded transition-colors" data-id="element-5239">
                Reset to Default
              </button>
              <button onClick={() => setShowConfigModal(false)} className="px-4 py-2 text-sm font-medium text-white bg-[#7CB342] hover:bg-[#689F38] rounded transition-colors" data-id="element-5240">
                Done
              </button>
            </div>
          </div>
        </div>}
    </div>;
}